/**
 * Working offline, and updating.
 *
 * The service worker keeps the app on the device, so it opens with no network.
 * A new version is downloaded in the background and then waits: the page tells
 * the user and asks, and never reloads on its own over a document someone is
 * typing in. If anything is unsaved, the question says so, and the work is kept
 * in the journal and offered back after the reload.
 *
 * The registration function is passed in (`virtual:pwa-register` in the app,
 * a stand-in in tests), so none of this needs a browser's service worker to
 * be tested.
 */
import { dismiss, notes, notify } from '../stores/notifications'
import { anyDirty, isDirty, journalKey, useDocuments } from '../stores/documents'
import { showNotice } from '../stores/ui'
import { flushAll } from '../editor/pool'
import { logError } from '../utils/report'
import { leaving } from './leaving'

export interface RegisterOptions {
  immediate?: boolean
  onNeedRefresh?: () => void
  onOfflineReady?: () => void
  onRegisteredSW?: (url: string, registration: ServiceWorkerRegistration | undefined) => void
  onRegisterError?: (error: unknown) => void
}
export type Register = (options: RegisterOptions) => (reload?: boolean) => Promise<void>

/** How often a visit that stays open looks for a new version. */
const CHECK_EVERY_MS = 60 * 60 * 1000
/** How long to wait for a new version to finish downloading when asked to check. */
const INSTALL_WAIT_MS = 20_000
/** How long to wait for the new version to take over before reloading anyway. */
const TAKEOVER_WAIT_MS = 5000

/**
 * The two browser moves an update ends with, passed in so tests need neither a
 * worker nor a real reload.
 *
 * The reload is the page's own. The registration library reloads when a new
 * worker takes control, but wires that up only for a version it announced
 * itself, and not for one found by Check Updates: there the new worker took
 * over and the page sat on the old version.
 */
export interface PwaHooks {
  reload(): void
  /** Resolves when the new worker controls the page, or after a while. */
  takeover(): Promise<void>
}

const browserHooks: PwaHooks = {
  reload: () => location.reload(),
  takeover: () =>
    new Promise<void>((resolve) => {
      navigator.serviceWorker?.addEventListener('controllerchange', () => resolve(), { once: true })
      setTimeout(resolve, TAKEOVER_WAIT_MS)
    }),
}

export interface Pwa {
  /** Help ▸ Check Updates. */
  checkForUpdate(): Promise<void>
}

export function setupPwa(register: Register, hooks: PwaHooks = browserHooks): Pwa {
  let registration: ServiceWorkerRegistration | undefined
  let applyUpdate: (reload?: boolean) => Promise<void> = async () => {}

  /** Takes the offer of an update off the screen. */
  const withdraw = (): void => {
    const offer = notes.find((n) => n.key === 'update')
    if (offer) dismiss(offer.id)
  }

  /** Updates once the user has said yes, asking first when work would be at stake. */
  async function update(): Promise<void> {
    // What is on screen, not what the store caught 200 ms ago.
    flushAll()
    if (anyDirty()) {
      const yes = await window.api.app.confirm(
        'Update now?',
        'The page reloads to take the update. Your unsaved changes are kept, and offered back afterwards.',
        'Update',
        'Later'
      )
      if (!yes) return
    }
    // Unsaved work to the journal, and stored, before the page goes: it is
    // offered back afterwards. Not left to the usual pause in typing.
    for (const d of useDocuments().docs.filter(isDirty))
      window.api.file.journal(journalKey(d), d.content)
    await window.api.web.flushJournals()
    leaving.confirmed = true
    try {
      // Listening before asking, so the takeover cannot be missed.
      const takenOver = hooks.takeover()
      await applyUpdate(false)
      await takenOver
      hooks.reload()
    } catch (err) {
      leaving.confirmed = false
      logError('apply update', err)
    }
  }

  /** How many times an update has been offered, so a check can tell whether one came meanwhile. */
  let offers = 0

  /**
   * The waiting workers the user has put off. The registration library may
   * announce one version more than once, a moment apart, and an offer that comes
   * back right after Later is not Later.
   */
  const declined = new WeakSet<object>()

  /**
   * One offer, however many times a version is found: the same note is updated.
   * `asked` is for when the user asked to look (Check Updates), which is answered
   * even for a version they put off earlier.
   */
  function offer(asked = false): void {
    const waiting = registration?.waiting
    if (!asked && waiting && declined.has(waiting)) return
    offers++
    notify('A new version of ekram.md is ready.', {
      key: 'update',
      timeoutMs: 0,
      actions: [
        { label: 'Update', run: () => void update() },
        {
          label: 'Later',
          run: () => {
            const waiting = registration?.waiting
            if (waiting) declined.add(waiting)
            withdraw()
          },
        },
      ],
    })
  }

  applyUpdate = register({
    immediate: true,
    onNeedRefresh: offer,
    onOfflineReady: () => notify('ekram.md is ready to work offline.', { key: 'offline-ready' }),
    onRegisteredSW: (_url, reg) => {
      registration = reg
      // A tab kept open for days still hears of a new version.
      if (reg) setInterval(() => void reg.update().catch(() => {}), CHECK_EVERY_MS)
    },
    // Not an error: a browser that will not run a service worker (a private
    // window, a policy) simply cannot keep the app offline. The page works.
    onRegisterError: (err) =>
      console.warn(
        'ekram.md: the service worker did not start, so the app will not work offline.',
        err
      ),
  })

  return {
    async checkForUpdate() {
      const reg = registration ?? (await navigator.serviceWorker?.getRegistration())
      if (!reg) {
        showNotice('Updates are not available here: this browser is not keeping the app offline.')
        return
      }
      const offersBefore = offers
      const checking = notify('Checking for a new version…', { key: 'update-check', timeoutMs: 0 })
      try {
        await reg.update()
      } catch {
        notify('Could not check for a new version. Are you offline?', {
          kind: 'error',
          key: 'update-check',
        })
        return
      }
      // A new version takes a moment to download after it is found.
      const installing = reg.installing
      if (installing) {
        await new Promise<void>((resolve) => {
          const done = (): void => {
            if (installing.state === 'installed' || installing.state === 'redundant') resolve()
          }
          installing.addEventListener('statechange', done)
          setTimeout(resolve, INSTALL_WAIT_MS)
          done()
        })
      }
      if (reg.waiting) {
        // The registration usually announces a new version itself, while this check
        // waits; offering again then would bring back an offer the user has just
        // put off. Only when nothing was offered meanwhile is it offered here.
        if (offers === offersBefore) offer(true)
        dismiss(checking)
      } else {
        notify('You have the latest version.', { key: 'update-check' })
      }
    },
  }
}
